import kebabCase from 'lodash-es/kebabCase'
import React, { useEffect, useState } from 'react'

import { Button, ButtonClean, Icon, useMediaQueryContext } from '@fourthwall/components'
import { ArrowShortDownIcon, TickIcon } from '@fourthwall/icons'

import * as S from './styled'
import type { OnboardingListProps } from './types'

export const OnboardingList = ({ items }: OnboardingListProps) => {
  const { tablet } = useMediaQueryContext()
  const [openedIndex, setOpenedIndex] = useState(items.findIndex((item) => !item.completed) || 0)

  useEffect(() => {
    if (items[openedIndex]?.completed) {
      setOpenedIndex(items.findIndex((item) => !item.completed) || 0)
    }
  }, [items, openedIndex])

  return (
    <S.Container>
      {items.map((item, index) => {
        const isOpen = openedIndex === index && !item.completed

        const getAppearance = () => {
          if (item.completed) return 'success'

          if (isOpen) return 'active'

          return 'normal'
        }

        const renderItemNumber = () => (
          <S.ItemNumberContainer>
            <S.ItemNumber $appearance={getAppearance()}>
              {item.completed ? <Icon component={TickIcon} height={12} /> : index + 1}
            </S.ItemNumber>
          </S.ItemNumberContainer>
        )

        const handleClick = () => {
          if (!isOpen && !item.completed) {
            setOpenedIndex(index)
          }
        }

        return (
          <S.ItemContainer
            key={kebabCase(item.title)}
            $completed={!!item.completed}
            $open={isOpen}
            $isNextOpen={openedIndex === index + 1 && !items[index + 1]?.completed}
            $last={index === items.length - 1}
            onClick={handleClick}
          >
            {tablet && renderItemNumber()}
            <S.ItemRow>
              <S.ItemMain>
                <S.ItemHeader $float={!!item.node}>
                  <div>
                    <S.ItemTitleWrapper>
                      <S.ItemTitle $active={isOpen}>
                        {!tablet && renderItemNumber()}
                        {item.title}
                      </S.ItemTitle>
                      <S.ItemArrow>
                        <Icon component={ArrowShortDownIcon} height={tablet ? 16 : 12} />
                      </S.ItemArrow>
                      {item.completed && item.completedButton && (
                        <S.ItemCompletedButtonContainer>
                          <ButtonClean {...item.completedButton} appearance="secondary" />
                        </S.ItemCompletedButtonContainer>
                      )}
                    </S.ItemTitleWrapper>
                    {isOpen && item.description && (
                      <S.ItemDescription>{item.description}</S.ItemDescription>
                    )}
                  </div>
                  {isOpen && (
                    <S.ItemFooter $float={!!item.node}>
                      <Button
                        appearance="primary"
                        size="small"
                        {...item.primaryButton}
                        fullWidth={!!item.secondaryButton}
                        analytics="checklistStep"
                      />
                      {item.onSkip && (
                        <S.ItemSkipButton onClick={item.onSkip} data-analytics="skipChecklistStep">
                          {tablet ? 'Skip this step' : 'Skip'}
                        </S.ItemSkipButton>
                      )}
                      {item.secondaryButton && (
                        <Button
                          appearance="secondary"
                          size="small"
                          {...item.secondaryButton}
                          fullWidth
                        />
                      )}
                    </S.ItemFooter>
                  )}
                </S.ItemHeader>
                {isOpen && item.node && item.node}
              </S.ItemMain>
              {isOpen && tablet && item.imageUrl && <S.ItemImage src={item.imageUrl} />}
            </S.ItemRow>
          </S.ItemContainer>
        )
      })}
    </S.Container>
  )
}
